// ═════════════════════════════════════════════════════════════════════════════
//  LA RUTINA DE HOY · prefijo crt-
//
//  Rob vio la primera version y dijo: «el diseño no me gustó, más bien el modal,
//  me gustaría que fuera como el de configuración, ese formato se ve mejor».
//
//  ── QUE SE COPIA, PIEZA POR PIEZA ───────────────────────────────────────────
//  El panel del engrane tiene una gramatica muy concreta, y es la que se sigue
//  aqui:
//
//    · Rotulo de seccion en versalitas pequeñas y grises.   (.cch-panel-tit)
//    · Un GRUPO HUNDIDO con las opciones dentro, en pildoras. (.cch-idiomas)
//    · La activa, rellena de amarillo con texto negro.        (.cch-idioma-on)
//    · Y UNA LINEA DE AYUDA DEBAJO que describe SOLO la activa. (.cch-panel-pie)
//
//  Esa ultima es la pieza que mas cambia el resultado. Antes cada estacion
//  llevaba su porque escrito al lado: cuatro razones compitiendo a la vez. En
//  los ajustes solo se explica la opcion elegida —«Español con inglés dentro»—
//  y por eso el panel se lee de un golpe. Aqui igual: la linea de abajo cuenta
//  la estacion que TOCA AHORA y las demas esperan calladas.
//
//  Por lo mismo desaparecen los cuatro colores por estacion. En el panel manda
//  un solo acento, el amarillo de NSL; cuatro colores serian justo lo que
//  rompe el formato que se viene a copiar.
//
//  ── LO QUE NO CAMBIA ────────────────────────────────────────────────────────
//  No dice «aprobado» en ninguna parte. Una estacion se marca cuando el alumno
//  la ABRE desde aqui, que es lo unico que se puede medir: los minijuegos no
//  guardan resultado por partida.
// ═════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

import "./CoachRutina.css";

const ICONO = {
  vocabulario: "🔤",
  clase: "📘",
  minijuego: "🎮",
  hablar: "🎤",
};

export default function CoachRutina({ rutina, historial, textos,
                                     onAbrir, onPedir, onDescartar, onCerrar }) {
  const T = textos;
  const cajaRef = useRef(null);
  // Que decision esta pendiente de confirmar: null | "cambiar" | "descartar".
  // Rob: «necesitamos una pregunta de seguridad... eso hace pensar al alumno si
  // realmente quiere o no». Las dos son decisiones que deshacen trabajo del
  // coach y una de ellas se lleva la rutina de la pantalla; un solo toque para
  // eso es demasiado poco toque.
  const [confirmando, setConfirmando] = useState(null);

  useEffect(() => {
    const salir = ev => { if (ev.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", salir);
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cajaRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", salir);
      document.body.style.overflow = antes;
    };
  }, [onCerrar]);

  if (!rutina?.pasos?.length) return null;

  const pasos = rutina.pasos;
  const hechos = pasos.filter(p => p.hecho_a_las).length;
  const total = pasos.length;
  const completa = hechos === total;

  // La siguiente sin hacer: la unica rellena de amarillo, igual que en el panel
  // solo hay un idioma marcado.
  const siguiente = pasos.find(p => !p.hecho_a_las);

  const restan = pasos.filter(p => !p.hecho_a_las)
                      .reduce((s, p) => s + (p.minutos || 0), 0);

  const dias = historial?.dias || [];

  return (
    <div className="crt-fondo" onClick={onCerrar}>
      <div
        className="crt-hoja"
        role="dialog"
        aria-modal="true"
        aria-label={T.rutinaTitulo}
        tabIndex={-1}
        ref={cajaRef}
        onClick={ev => ev.stopPropagation()}
      >
        <button className="crt-cerrar" onClick={onCerrar} aria-label={T.rutinaCerrar}>
          <X size={16} />
        </button>

        {/* ── LA RUTINA ─────────────────────────────────────────────────────
            Rotulo, grupo hundido, linea de ayuda: el mismo bloque de tres
            piezas que el idioma y el trato en los ajustes. */}
        {/* Cerrada, el rotulo se convierte en el titular de la fiesta. El
            confeti lo lanza la pantalla al abrir la hoja; aqui va lo que se
            queda leyendose despues, que es lo que de verdad hay que leer. */}
        {completa ? (
          <p className="crt-rotulo crt-rotulo-fiesta">
            🏆 {T.rutinaTerminada}
            <span className="crt-cuenta">{T.rutinaHechas(hechos, total)}</span>
          </p>
        ) : (
          <p className="crt-rotulo">
            {T.rutinaTitulo}
            <span className="crt-cuenta">{T.rutinaHechas(hechos, total)}</span>
          </p>
        )}

        <div className="crt-grupo">
          {pasos.map(p => {
            const hecho = Boolean(p.hecho_a_las);
            const activa = p.id === siguiente?.id;
            return (
              <button
                key={p.id}
                className={"crt-fila"
                  + (activa ? " crt-fila-activa" : "")
                  + (hecho ? " crt-fila-hecha" : "")}
                onClick={() => onAbrir(p)}
                aria-label={`${T.rutinaFases[p.tipo]} · ${p.titulo} · ${T.rutinaPorque(p.porque, p.dato)}`}
              >
                <span className="crt-icono" aria-hidden="true">{ICONO[p.tipo] || "•"}</span>
                <span className="crt-nombre">
                  <span className="crt-fase">{T.rutinaFases[p.tipo]}</span>
                  <span className="crt-titulo-paso">{p.titulo}</span>
                </span>
                {/* A la derecha, el estado: la palomita si la cerro y, si no,
                    lo que dura. Es el sitio que ocupa el interruptor de la voz
                    en el panel de ajustes. */}
                <span className="crt-estado" aria-hidden="true">
                  {hecho ? <Check size={14} strokeWidth={3.5} />
                         : T.rutinaMin(p.minutos)}
                </span>
              </button>
            );
          })}
        </div>

        {/* La ayuda de la ACTIVA, como «Español con inglés dentro» describe el
            idioma marcado. Una sola razon a la vista, no cuatro. */}
        <p className="crt-pie">
          {completa
            ? T.rutinaBravo
            : `${T.rutinaPorque(siguiente.porque, siguiente.dato)} · ${T.rutinaRestan(restan)}`}
        </p>

        {/* ── LO QUE PUEDE HACER AHORA ──────────────────────────────────────
            Las opciones cambian con el estado, porque las que sirven son
            distintas. Al que acaba de terminar no se le ofrece "descártala"
            —¿descartar que, si ya la hizo?— y al que va por la mitad no se le
            ofrece "ponme otra" como si hubiera acabado.

            Las dos que hablan con el coach cierran la hoja y le mandan un
            mensaje. El alumno vuelve a la conversacion y LE VE trabajar: el
            haz recorriendo el borde y los mensajes de razonamiento que ya
            existen. Esta vez mirar la espera tiene premio, porque al final
            aparece su rutina nueva y el boton de arriba vuelve a latir. */}
        {/* ── LA PREGUNTA DE SEGURIDAD ───────────────────────────────────
            Sustituye a los botones en vez de aparecer encima: asi no hay forma
            de pulsar "Descártala" por segunda vez sin querer, que es como se
            confirman las cosas sin leerlas.

            Y dice LA VERDAD de lo que pasa. Ninguna de las dos borra su
            progreso —cambiar conserva lo cerrado, descartar lo deja en el
            historial— asi que ninguna de las dos se lo dice. Asustarle con
            algo que no ocurre gasta el aviso para cuando haga falta. */}
        {confirmando ? (
          <div className="crt-confirma">
            <p className="crt-confirma-aviso">
              {confirmando === "cambiar"
                ? T.rutinaCambiarAviso(hechos, total)
                : T.rutinaDescartarAviso(hechos, total)}
            </p>
            <div className="crt-opciones">
              <button
                className={"crt-opcion crt-opcion-fuerte"
                  + (confirmando === "descartar" ? " crt-opcion-roja" : "")}
                onClick={() => {
                  const era = confirmando;
                  setConfirmando(null);
                  if (era === "cambiar") onPedir(T.rutinaCambialaMsg);
                  else onDescartar();
                }}
              >
                {confirmando === "cambiar" ? T.rutinaSiCambia : T.rutinaSiDescarta}
              </button>
              <button className="crt-opcion crt-opcion-suave"
                      onClick={() => setConfirmando(null)}>
                {T.rutinaMejorNo}
              </button>
            </div>
          </div>
        ) : (
        <div className="crt-opciones">
          {completa ? (
            <>
              <button className="crt-opcion crt-opcion-fuerte"
                      onClick={() => onPedir(T.rutinaOtraMsg)}>
                🔁 {T.rutinaOtra}
              </button>
              <button className="crt-opcion"
                      onClick={() => onPedir(T.rutinaComoVoyMsg)}>
                📊 {T.rutinaComoVoy}
              </button>
              <button className="crt-opcion crt-opcion-suave" onClick={onCerrar}>
                {T.rutinaPorHoyVale}
              </button>
            </>
          ) : (
            <>
              <button className="crt-opcion"
                      onClick={() => setConfirmando("cambiar")}>
                🔄 {T.rutinaCambiala}
              </button>
              {/* En rojo desde el principio: el color es la primera advertencia
                  y llega antes que la pregunta. Un boton que se lleva algo por
                  delante no puede parecerse a los que no. */}
              <button className="crt-opcion crt-opcion-roja"
                      onClick={() => setConfirmando("descartar")}>
                🗑️ {T.rutinaDescarta}
              </button>
            </>
          )}
        </div>
        )}

        {/* ── EL HISTORIAL ──────────────────────────────────────────────────
            Segundo bloque del panel, con su aire por encima —igual que "TRATO"
            debajo de "IDIOMA"—.

            Es un REGISTRO de lo que hizo, nunca una lista de deberes: los dias
            que no entreno sencillamente no salen. Un hueco es un dato neutro;
            una casilla en cero un martes que ni abrio la app es un reproche. */}
        {dias.length > 0 && (
          <>
            <p className="crt-rotulo crt-rotulo-2">
              {T.rutinaHistorial}
              {historial.racha_completas > 0 && (
                <span className="crt-cuenta">
                  {T.rutinaRacha(historial.racha_completas)}
                </span>
              )}
            </p>

            <div className="crt-grupo crt-tira">
              {/* Del mas antiguo al mas reciente: el tiempo se lee hacia la
                  derecha y el backend los manda al reves. */}
              {dias.slice(0, 10).reverse().map(d => (
                <span
                  key={d.fecha}
                  className={"crt-dia" + (d.completa ? " crt-dia-completa" : "")}
                  title={T.rutinaDia(d.fecha, d.hechas, d.total)}
                  aria-label={T.rutinaDia(d.fecha, d.hechas, d.total)}
                >
                  {d.hechas}
                </span>
              ))}
            </div>

            <p className="crt-pie">
              {T.rutinaResumen(historial.completas, historial.estaciones_cerradas)}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
